import React, { FC, ReactNode } from 'react';
import {
  Box, Typography, Chip, Tooltip, Stack, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTheme } from '@mui/material/styles';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutorenewIcon from '@mui/icons-material/Autorenew';

interface ProviderOrgRowProps {
  displayName: string | null;
  address?: string;
  // Right-side slot — a status chip and/or an action button (Connect / Skip / ✓ Connected / Automatic).
  // Kept as a slot so this component stays layout-only and provider-class-agnostic (Epic connect vs Kno2
  // network), reusable across the onboarding "Find my providers" screen and Settings.
  trailing?: ReactNode;
  // Status chip shown inline NEXT TO the name (both onboarding + post-onboarding). Separate from `trailing`
  // (the action slot) so the status label sits with the name while the action (Connect/Skip or Refresh) sits right.
  statusChip?: ReactNode;
  // When set, the row is clickable (opens the org detail card). Trailing buttons should stopPropagation.
  onClick?: () => void;
  // Highlight this row as the currently-selected / "up next" org (purple ring + tint). Used by the guided
  // connect flow's "See all" list to mark which org the loop is on.
  selected?: boolean;
  // Epic orgs only: patient-facing MyChart base URL (derived server-side from the org's authorize-redirect).
  // When set, render a "Create / reset your MyChart account" external link under the address so a patient who
  // lacks an account (or forgot their password) can fix that in a new tab before connecting. See EpicFhirOrg.
  myChartUrl?: string | null;
  // Stamped as `data-org-key` so a screen can bring one row into view — see scrollOrgIntoView. Set by
  // ProviderOrgList for every row; there is no reason to pass it by hand.
  rowKeyAttr?: string;
}

/**
 * Shared one-line display for a discovered provider org (icon + name/address + a trailing action/status
 * slot). Purely presentational so it can be reused wherever we surface discovered orgs — the onboarding
 * providers screen renders epicFhirOrgs (Connect) and kno2Orgs (informational) through it, and Settings
 * can adopt it later. See project_hw_unified_data_retrieval.
 */
export const ProviderOrgRow: FC<ProviderOrgRowProps> = ({ displayName, address, statusChip, trailing, onClick, selected, myChartUrl, rowKeyAttr }) => {
  const theme = useTheme();
  // Container-query responsive: the row reacts to its OWN rendered width (its parent container), NOT the
  // viewport — so the narrow Settings drawer + the connect card (both < md) get the stacked/no-icon layout,
  // while the wide records screen gets the inline layout. Threshold = MUI's `md` breakpoint (named, not a
  // magic px — Leo). The outer Box is the query container; children switch to the WIDE layout at/above it.
  const wide = `@container (min-width: ${theme.breakpoints.values.md}px)`;
  return (
    <Box sx={{ containerType: 'inline-size' }} data-org-key={rowKeyAttr}>
      {/* Card = a COLUMN: the org row on top, and (when present) the full-width MyChart link along the
          bottom — below BOTH the address and the action buttons, so it never squeezes beside them (Leo). */}
      <Box
        onClick={onClick}
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          ...(onClick ? { cursor: 'pointer', '&:hover': { borderColor: '#d7d3f0', bgcolor: '#f7f7fa' } } : {}),
          ...(selected ? { borderColor: '#6c4ce0', bgcolor: '#efeaff', boxShadow: '0 0 0 2px rgba(108,76,224,0.25)' } : {}),
        }}
      >
      {/* Narrow (container query, same threshold as the rest of the row): the trailing actions WRAP onto
          their own right-justified line below the name/address; wide keeps them inline on the right. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, rowGap: 1, flexWrap: 'wrap', [wide]: { flexWrap: 'nowrap' } }}>
        {/* Leading icon — hidden on the narrow (stacked) layout to free width for the name; shown when wide. */}
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            flexShrink: 0,
            bgcolor: '#f0f9ff',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            [wide]: { display: 'flex' },
          }}
        >
          <LocalHospitalIcon sx={{ color: '#0284c7', fontSize: 24 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {/* Narrow: STACKED — status chip on TOP-RIGHT (column-reverse → the 2nd child renders first),
              then the name, then the address, left-justified; all wrap, nothing truncates.
              Wide: one line — chip sits RIGHT NEXT TO the org name (Leo), name ellipsizes. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column-reverse',
              alignItems: 'flex-start',
              gap: 0.5,
              minWidth: 0,
              [wide]: { flexDirection: 'row', alignItems: 'center', gap: 1 },
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                color: '#1c1c1e',
                minWidth: 0,
                whiteSpace: 'normal',
                [wide]: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
            >
              {displayName || 'Unknown provider'}
            </Typography>
            {statusChip ? <Box sx={{ flexShrink: 0, alignSelf: 'flex-end', [wide]: { alignSelf: 'center' } }}>{statusChip}</Box> : null}
          </Box>
          {address ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'normal', [wide]: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
            >
              {address}
            </Typography>
          ) : null}
        </Box>
        {trailing ? (
          <Box sx={{ flexBasis: '100%', display: 'flex', justifyContent: 'flex-end', [wide]: { flexBasis: 'auto', flexShrink: 0 } }}>
            {trailing}
          </Box>
        ) : null}
      </Box>
      {/* Epic orgs only: link to the org's MyChart (create account / reset password), opens in a new
          tab. Full-width along the card bottom (below address AND action buttons — Leo). */}
      {myChartUrl ? (
        <Button
          component="a"
          href={myChartUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          size="small"
          fullWidth
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          sx={{ mt: 1, textTransform: 'none', fontSize: '0.72rem', fontWeight: 600, color: '#6c4ce0' }}
        >
          Create or reset your MyChart account
        </Button>
      ) : null}
      </Box>
    </Box>
  );
};

// Shared post-onboarding per-org action button (records + Settings) — token-first re-pull for one org,
// falling back to the MyChart login redirect when the org needs auth. Labeled "Get Health Records"
// (Leo 2026-07-12, universal wording): truthful for BOTH states — authed → re-pull; needs_auth →
// log in, then pull — so one button serves every org without implying a doomed token refresh. NO custom
// css so it renders IDENTICALLY in both places (Leo); callers pass behavior only (busy/disabled/onClick).
// `label` overrides the default for an org that has never been connected: the same click then sends
// the patient to that provider's MyChart FIRST and retrieves afterwards, so "Get Health Records"
// alone describes only half of what is about to happen. The behaviour is unchanged — only the promise.
export const ProviderRefreshButton: FC<{ busy?: boolean; disabled?: boolean; label?: string; onClick: () => void }> = ({ busy, disabled, label, onClick }) => (
  <Button
    size="small"
    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
    disabled={disabled}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    // A wrapped label is why the long variant looked centred with the icon stranded above it — MUI
    // centres a Button's content, so two lines re-centre the whole thing. Keep the action on one
    // line and let the provider NAME truncate instead: the name has a flexible column, the button
    // does not, and a half-shown action is worse than a half-shown name.
    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
  >
    {label ?? 'Get Health Records'}
  </Button>
);

// ---- Per-org run/connect OUTCOME — shared aggregation for the "Your providers" displays ----
export type OrgOutcome = 'connected' | 'authed' | 'auto' | 'needs_connect' | 'error' | 'no_records' | 'pending' | 'skipped';

// Derive ONE outcome from an org's stored auth + retrieval fields. Reads the org's OWN runStatus — for an
// Epic org that's the FHIR result, so its (harmless) XCA failure is never considered. kno2 orgs have no
// `status` (no auth axis) → outcome comes straight from their XCA runStatus.
export function deriveOrgOutcome(org: {
  status?: 'authed' | 'needs_auth' | 'skipped';
  runStatus?: 'to_be_processed' | 'succeeded' | 'failed';
  documentsRetrieved?: number;
}): OrgOutcome {
  if (org.status === 'skipped') return 'skipped';
  if (org.status === 'needs_auth') return 'needs_connect';
  if (org.runStatus === 'failed') return 'error';
  if (org.runStatus === 'succeeded') return (org.documentsRetrieved ?? 0) > 0 ? 'connected' : 'no_records';
  if (org.status === 'authed') return 'authed'; // epic org connected, retrieval not yet run (pre-"Get my records")
  // Default when no explicit outcome applies — notably kno2/network orgs (no auth axis): they're pulled for the
  // user automatically, so label them "Retrieved automatically" until an actual retrieval result lands.
  return 'auto';
}

/**
 * Label for the per-org retrieve button. An org that is NOT connected holds no Epic token, so the click
 * falls through to the MyChart login and only then retrieves — the label has to say both, or it promises
 * a retrieval that cannot happen (Leo 2026-10-04).
 *
 * ★ "Not connected" is BOTH `needs_connect` AND `skipped`, which is the part that was wrong: skipping an
 * org never authenticated it, yet it was getting the short label. Every other outcome — retrieved,
 * connected, no records, retrieval failed — did authenticate, so the click really is just a refresh.
 *
 * ONE helper because two screens render this button (Settings and the records provider list) and the
 * rule must not drift between them.
 */
export const providerRetrieveLabel = (org: Parameters<typeof deriveOrgOutcome>[0]): string =>
  ['needs_connect', 'skipped'].includes(deriveOrgOutcome(org))
    ? 'Connect & Get Health Records'
    : 'Get Health Records';

const OUTCOME_META: Record<OrgOutcome, { Icon: typeof CheckCircleIcon; label: string; color: string; bg?: string }> = {
  connected:     { Icon: CheckCircleIcon,         label: 'Retrieved',  color: '#16a34a' },
  authed:        { Icon: CheckCircleIcon,         label: 'Connected',  color: '#16a34a' },
  auto:          { Icon: AutorenewIcon,           label: 'Retrieved automatically', color: '#16a34a' },
  needs_connect: { Icon: InfoOutlinedIcon,        label: 'Provider login needed',  color: '#6c4ce0', bg: '#efeaff' },
  error:         { Icon: ErrorIcon,               label: 'Retrieval failed', color: '#c0736b', bg: '#fbe6e3' },
  no_records:    { Icon: RemoveCircleOutlineIcon, label: 'No records', color: '#6b7280' },
  pending:       { Icon: HourglassEmptyIcon,      label: 'Pending',    color: '#6b7280' },
  skipped:       { Icon: BlockIcon,               label: 'Skipped',    color: '#b45309', bg: '#fef3c7' },
};

// Colored status chip for one org's outcome; `reason` (e.g. lastError) shows as a tooltip on error.
export const OrgOutcomeChip: FC<{ outcome: OrgOutcome; reason?: string | null }> = ({ outcome, reason }) => {
  const m = OUTCOME_META[outcome];
  const Icon = m.Icon;
  // 'Provider login needed' is the one outcome whose label implies an ACTION, so the outlined border makes
  // it read as a (mis)clickable button — but the chip is status-only. Render it borderless (soft filled
  // pill) just for that outcome; every other outcome keeps the outline, which reads fine (Leo 2026-09-02).
  const borderless = outcome === 'needs_connect';
  const chip = (
    <Chip
      icon={<Icon sx={{ color: `${m.color} !important`, fontSize: 18 }} />}
      label={m.label}
      size="small"
      variant={borderless ? 'filled' : 'outlined'}
      sx={{ color: m.color, fontWeight: 600, ...(borderless ? {} : { borderColor: m.color }), ...(m.bg ? { bgcolor: m.bg } : {}) }}
    />
  );
  // Hard failure: the (red) chip IS the signal; its tooltip carries the raw reason.
  if (outcome === 'error') return reason ? <Tooltip title={reason} arrow>{chip}</Tooltip> : chip;
  // Non-fatal: the org was reached but SOME identifier queries errored (reason present on a non-error outcome)
  // → keep the normal chip + a subtle, non-threatening ⓘ inviting a click for the details (raw text lives in
  // the row's detail dialog).
  if (reason) return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {chip}
      <Tooltip title="Some records may not have been retrieved — see details" arrow>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: '#c2803d' }} />
      </Tooltip>
    </Box>
  );
  return chip;
};

// Fields ProviderOrgList reads off each org — the union of what EpicFhirOrg + Kno2Org expose that the row
// needs. Both types are structurally assignable to this (Kno2Org just omits the Epic-only optionals).
export type ProviderOrgLike = {
  displayName: string | null;
  address: string;
  requestId?: string | null;
  index: number;
  status?: 'authed' | 'needs_auth' | 'skipped';
  runStatus?: 'to_be_processed' | 'succeeded' | 'failed';
  documentsRetrieved?: number;
  lastError?: string | null;
  myChartUrl?: string | null;
  // The PATIENT named this provider via the "missing provider?" picker rather than Kno2 finding it.
  // Worth showing: it explains why the row is there, and it stays true even once Kno2 also matches
  // the org. See project_hw_epic_direct_connect.
  addedByUser?: boolean;
};

// Stable per-org key — used BOTH as the React key and for selection matching (so the connect screen can pass
// `selectedKey={orgKey(currentOrg)}` and the list highlights the same row).
export const orgKey = (org: ProviderOrgLike): string =>
  org.requestId != null ? `${org.requestId}:${org.index}` : `k:${org.index}`;

/**
 * Bring one org's row into view — used after the "missing provider?" picker adds an org (or reports one
 * was already there), so the patient SEES the row instead of a notice referring to something off-screen.
 * That confusion is the reason this exists: "added — connect it below" with the row below the fold reads
 * as nothing having happened.
 *
 * Deferred a frame because the caller has just patched the RTK cache and the row does not exist yet;
 * `requestAnimationFrame` lets React commit first. A miss is silent on purpose — scrolling is a nicety and
 * must never throw into an add that otherwise succeeded. See project_hw_epic_direct_connect.
 */
export const scrollOrgIntoView = (key: string): void => {
  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-org-key="${CSS.escape(key)}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
};

// The ONE list-level component: maps an orgs array → ProviderOrgRow per row, so every screen (onboarding
// connect, records, Settings) renders the SAME row (responsive icon/label sizing, status chip, MyChart button)
// without repeating the `.map()`. Callers pass only their data + the parts that vary: `selectedKey` (connect
// highlight), `renderTrailing` (per-screen action slot), `onOrgClick` (records detail). The status chip is
// built here (identical everywhere). See project_hw_unified_data_retrieval.
export function ProviderOrgList<T extends ProviderOrgLike>({
  orgs,
  selectedKey,
  renderTrailing,
  onOrgClick,
  showMyChart = true,
  spacing = 1.5,
}: {
  orgs: T[];
  selectedKey?: string | null;
  renderTrailing?: (org: T) => ReactNode;
  onOrgClick?: (org: T) => void;
  showMyChart?: boolean;
  spacing?: number;
}) {
  return (
    <Stack spacing={spacing}>
      {orgs.map((org) => {
        const key = orgKey(org);
        const outcome = deriveOrgOutcome(org);
        return (
          <ProviderOrgRow
            key={key}
            rowKeyAttr={key}
            displayName={org.displayName}
            address={org.address}
            selected={selectedKey != null && key === selectedKey}
            statusChip={
              <>
                <OrgOutcomeChip outcome={outcome} reason={org.lastError} />
                {/* Explains why a row the patient does not remember us finding is in their list. */}
                {org.addedByUser && (
                  <Chip size="small" variant="outlined" label="Added by you"
                        sx={{ ml: 0.5, fontSize: '0.68rem', height: 20, borderColor: '#d7d9e3', color: '#6b6b70' }} />
                )}
              </>
            }
            // MyChart account link only makes sense when the org still needs a login ('Provider login
            // needed') — for connected/auto/skipped orgs it's noise, so hide it (Leo 2026-07-24).
            myChartUrl={showMyChart && outcome === 'needs_connect' ? org.myChartUrl : undefined}
            trailing={renderTrailing?.(org)}
            onClick={onOrgClick ? () => onOrgClick(org) : undefined}
          />
        );
      })}
    </Stack>
  );
}

// Composite of the TWO provider lists — Epic (FHIR-REST, user-connectable) + Kno2 (network, auto-pulled) —
// passed as SEPARATE props and rendered as two ProviderOrgLists in one Stack. Screens that show both
// (onboarding intro / completion / connect-all, records) use this so the pair renders identically without
// repeating two <ProviderOrgList>s. No sub-header: the Kno2 orgs are distinguished by their per-org
// "Retrieved automatically" chip (deriveOrgOutcome's default outcome). Epic-only surfaces (Settings) use
// <ProviderOrgList> directly. See project_hw_unified_data_retrieval.
export function ProviderOrgLists<E extends ProviderOrgLike, K extends ProviderOrgLike>({
  epicOrgs,
  kno2Orgs,
  selectedKey,
  renderEpicTrailing,
  onOrgClick,
  showMyChart = true,
  spacing = 1.5,
}: {
  epicOrgs: E[];
  kno2Orgs: K[];
  selectedKey?: string | null;
  renderEpicTrailing?: (org: E) => ReactNode;
  onOrgClick?: (org: E | K) => void;
  showMyChart?: boolean;
  spacing?: number;
}) {
  // Errored providers (connection failures + failed retrievals → the red 'error' outcome) sink to the
  // BOTTOM of the combined list (Leo): working providers first, failures last. Partition each list;
  // filter preserves order within each group, and Epic keeps its trailing (Connect/Login) in BOTH of
  // its groups. Render order = Epic-ok, Kno2-ok, Epic-err, Kno2-err → all failures grouped at the bottom.
  const isErr = (o: ProviderOrgLike) => deriveOrgOutcome(o) === 'error';
  const epicOk = epicOrgs.filter((o) => !isErr(o));
  const epicErr = epicOrgs.filter((o) => isErr(o));
  const kno2Ok = kno2Orgs.filter((o) => !isErr(o));
  const kno2Err = kno2Orgs.filter((o) => isErr(o));
  return (
    <Stack spacing={spacing}>
      {epicOk.length > 0 ? (
        <ProviderOrgList orgs={epicOk} selectedKey={selectedKey} renderTrailing={renderEpicTrailing} onOrgClick={onOrgClick} showMyChart={showMyChart} spacing={spacing} />
      ) : null}
      {kno2Ok.length > 0 ? (
        <ProviderOrgList orgs={kno2Ok} onOrgClick={onOrgClick} spacing={spacing} />
      ) : null}
      {epicErr.length > 0 ? (
        <ProviderOrgList orgs={epicErr} selectedKey={selectedKey} renderTrailing={renderEpicTrailing} onOrgClick={onOrgClick} showMyChart={showMyChart} spacing={spacing} />
      ) : null}
      {kno2Err.length > 0 ? (
        <ProviderOrgList orgs={kno2Err} onOrgClick={onOrgClick} spacing={spacing} />
      ) : null}
    </Stack>
  );
}

// (ProviderLegend REMOVED 2026-07-12 — the chips carry their own labels, so a legend repeating the
// same text was redundant. Leo.)

// Human one-liner per outcome (detail card header).
const OUTCOME_SUMMARY: Record<OrgOutcome, string> = {
  connected: 'Records retrieved from this provider.',
  authed: 'Connected — records will be retrieved.',
  auto: 'These records are retrieved automatically from the network.',
  needs_connect: 'Log-in required to connect this provider.',
  error: 'We couldn’t retrieve records from this provider.',
  no_records: 'No records were found at this provider.',
  pending: 'Retrieval is in progress.',
  skipped: 'You skipped this provider.',
};

export interface OrgDetail {
  displayName: string | null;
  address?: string;
  status?: 'authed' | 'needs_auth' | 'skipped';
  runStatus?: 'to_be_processed' | 'succeeded' | 'failed';
  documentsRetrieved?: number;
  resourcesInserted?: number;
  lastError?: string | null;
  lastRunAt?: string | null;
}

// Per-org detail card (opens when a provider row is clicked): outcome summary + a "Technical details"
// accordion carrying the RAW HW error verbatim (deliberately NOT interpreted — the error is inconclusive)
// plus the key technical fields. View-only; the Connect/Reconnect action stays on the row.
export const OrgDetailDialog: FC<{ open: boolean; onClose: () => void; org: OrgDetail | null }> = ({ open, onClose, org }) => {
  if (!org) return null;
  const outcome = deriveOrgOutcome(org);
  const details = [
    `status:    ${org.status ?? '—'}`,
    `runStatus: ${org.runStatus ?? '—'}`,
    `documents: ${org.documentsRetrieved ?? 0}`,
    `resources: ${org.resourcesInserted ?? 0}`,
    `lastRunAt: ${org.lastRunAt ?? '—'}`,
    org.lastError ? `\nerror:\n${org.lastError}` : '',
  ].filter(Boolean).join('\n');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#1c1c1e' }}>{org.displayName || 'Unknown provider'}</DialogTitle>
      <DialogContent>
        {org.address ? <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{org.address}</Typography> : null}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <OrgOutcomeChip outcome={outcome} />
          <Typography variant="body2">{OUTCOME_SUMMARY[outcome]}</Typography>
        </Box>
        <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight={600}>Technical details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary' }}>
              {details}
            </Box>
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
